import { api } from "./api";

// ════════════════════════════════════════════════════════════════════════════
// Provider API Types
// ════════════════════════════════════════════════════════════════════════════

export interface Provider {
  id: string;
  userId: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  bio: string | null;
  photoUrl: string | null;
  isActive: boolean;
  services: Array<{ id: string; name: string }>;
  locations: Array<{ id: string; name: string }>;
  createdAt: string;
  updatedAt: string;
}

export interface ProviderScheduleEntry {
  id: string;
  providerId: string;
  locationId: string;
  locationName: string;
  dayOfWeek: number;
  startTime: string | null;
  endTime: string | null;
  isAvailable: boolean;
}

export interface TimeSlot {
  startTime: string;
  endTime: string;
}

// ════════════════════════════════════════════════════════════════════════════
// Provider CRUD
// ════════════════════════════════════════════════════════════════════════════

export async function getProviders(): Promise<Provider[]> {
  const response = await api<{ providers: Provider[] }>("/api/providers");
  return response.providers;
}

export async function createProvider(data: {
  name: string;
  email?: string;
  phone?: string;
  title?: string;
  bio?: string;
  photoUrl?: string;
  userId?: string;
  serviceIds?: string[];
  locationIds?: string[];
}): Promise<Provider> {
  const response = await api<{ provider: Provider }>("/api/providers", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return response.provider;
}

export async function updateProvider(
  id: string,
  data: {
    name?: string;
    email?: string | null;
    phone?: string | null;
    title?: string | null;
    bio?: string | null;
    photoUrl?: string | null;
    userId?: string | null;
    isActive?: boolean;
    serviceIds?: string[];
    locationIds?: string[];
  }
): Promise<Provider> {
  const response = await api<{ provider: Provider }>(`/api/providers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return response.provider;
}

export async function deleteProvider(id: string): Promise<void> {
  await api<{ success: boolean }>(`/api/providers/${id}`, {
    method: "DELETE",
  });
}

// ════════════════════════════════════════════════════════════════════════════
// Provider Schedule
// ════════════════════════════════════════════════════════════════════════════

export async function getProviderSchedule(
  providerId: string,
  locationId?: string
): Promise<ProviderScheduleEntry[]> {
  const params = locationId ? `?locationId=${locationId}` : "";
  const response = await api<{ schedule: ProviderScheduleEntry[] }>(
    `/api/providers/${providerId}/schedule${params}`
  );
  return response.schedule;
}

export async function updateProviderSchedule(
  providerId: string,
  schedule: Array<{
    dayOfWeek: number;
    startTime?: string | null;
    endTime?: string | null;
    isAvailable?: boolean;
  }>
): Promise<ProviderScheduleEntry[]> {
  const response = await api<{ schedule: ProviderScheduleEntry[] }>(
    `/api/providers/${providerId}/schedule`,
    {
      method: "PUT",
      body: JSON.stringify({ schedule }),
    }
  );
  return response.schedule;
}

// ════════════════════════════════════════════════════════════════════════════
// Availability
// ════════════════════════════════════════════════════════════════════════════

export async function getProviderAvailability(
  providerId: string,
  serviceId: string,
  locationId: string,
  date: string
): Promise<{ date: string; slots: TimeSlot[]; isAvailable: boolean }> {
  const params = new URLSearchParams({
    serviceId,
    locationId,
    date,
  });

  const response = await api<{ date: string; slots: TimeSlot[]; isAvailable: boolean }>(
    `/api/providers/${providerId}/availability?${params.toString()}`
  );
  return response;
}

